import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useEditorState } from '@tiptap/react';
import { ListIcon, ListOrderedIcon, ListTodoIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import type { DropdownOption } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { ListButtonProps, ListType } from '.';

export const listOptions: DropdownOption<ListType>[] = [
  {
    label: 'Lista punktowana',
    type: 'bulletList',
    icon: ListIcon,
  },
  {
    label: 'Lista numerowana',
    type: 'orderedList',
    icon: ListOrderedIcon,
  },
  {
    label: 'Lista zadań',
    type: 'taskList',
    icon: ListTodoIcon,
  },
];

export const listShortcutKeys: Record<ListType, string> = {
  bulletList: 'Ctrl-Shift-8',
  orderedList: 'Ctrl-Shift-7',
  taskList: 'Ctrl-Shift-9',
};

function useIsActiveElement(editor: Editor | null, type: ListType): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return ctx.editor?.isActive(type) ?? false;
      },
    }) ?? false
  );
}

function toggleList(editor: Editor | null, type: ListType): void {
  if (!editor) return;

  switch (type) {
    case 'bulletList':
      editor.chain().focus().toggleBulletList().run();
      break;
    case 'orderedList':
      editor.chain().focus().toggleOrderedList().run();
      break;
    case 'taskList':
      editor.chain().focus().toggleList('taskList', 'taskItem').run();
      break;
  }
}

function getOption(type: ListType): DropdownOption<ListType> | undefined {
  return listOptions.find((option) => option.type === type);
}

function useListButtonState(editor: Editor | null, type: ListType) {
  const option: DropdownOption<ListType> | undefined = getOption(type);
  const isActive: boolean = useIsActiveElement(editor, type);
  const shortcutKey: string = listShortcutKeys[type];

  return {
    option,
    isActive,
    shortcutKey,
  };
}

function useHandleClick(onClick: ListButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function ListButton(listButtonProps: ListButtonProps) {
  const { editor: providedEditor, type, onClick, children, className, ...props } = listButtonProps;
  const editor: Editor | null = useTiptapEditor(providedEditor);
  const { option, isActive } = useListButtonState(editor, type);

  const Icon = option?.icon || ListIcon;

  const handleClick = useHandleClick(onClick, () => toggleList(editor, type));

  const inSchema: boolean = isNodeInSchema(type, editor);
  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      aria-label={option?.label || type}
      aria-pressed={isActive}
      onClick={handleClick}
      className={cn('group aria-pressed:text-text-primary aria-pressed:bg-fill-secondary/60 aria-pressed:hover:bg-fill-secondary/80', className)}
      {...props}
    >
      {children || (
        <>
          <Icon className="group:aria-pressed:text-text-primary" />
          {option?.label || type}
        </>
      )}
    </DropdownMenuItem>
  );
}
